/**
 * Tune a space's floor (Prop Studio, Floors & lighting): the set, its tile
 * scale, turn, roughness, relief and brightness, seen live in the browser
 * (FloorPreview, Three.js) under one of the space's lights — no Blender, no
 * GPU worker. Save puts the placement back on the space's entry.
 */

import React, { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { DesignerModal } from '../designer/chrome/DesignerModal';
import { MiniSlider } from '../designer/render/controls';
import { DESIGNER_STRINGS } from '../designer/strings';
import { PLACEMENT_FACTORY, TexturePlacement } from '../ui/room3d/photoRender';
import type { HdriFinish } from '../ui/room3d/renderFinish';
import { FloorPreview } from './FloorPreview';
import type { HdriEntry, TextureSet } from './studioApi';
import { PROP_STUDIO_STRINGS } from './strings';

const F = PROP_STUDIO_STRINGS.finishes;
const P = DESIGNER_STRINGS.photo;
const T = DESIGNER_STRINGS.textureStudio;

const FIELDS: { key: keyof Omit<TexturePlacement, 'id'>; range: 'textures.scale' | 'textures.rotation' | 'textures.roughness' | 'textures.relief' | 'textures.brightness'; step: number; unit?: string }[] = [
  { key: 'scale', range: 'textures.scale', step: 0.05, unit: '×' },
  { key: 'rotation', range: 'textures.rotation', step: 1, unit: '°' },
  { key: 'roughness', range: 'textures.roughness', step: 0.01, unit: '×' },
  { key: 'relief', range: 'textures.relief', step: 0.01, unit: '×' },
  { key: 'brightness', range: 'textures.brightness', step: 0.01, unit: '×' },
];

export interface FloorTunerProps {
  /** The entry being tuned. */
  initial: TexturePlacement;
  /** The library's sets (the set can be swapped). */
  sets: TextureSet[];
  /** The space's lights, to see the floor under; the library's HDRIs for their names. */
  lights: HdriFinish[];
  hdris: HdriEntry[];
  spaceLabel: string;
  onSave: (placement: TexturePlacement) => void;
  onClose: () => void;
}

export const FloorTuner: React.FC<FloorTunerProps> = ({ initial, sets, lights, hdris, spaceLabel, onSave, onClose }) => {
  const [placement, setPlacement] = useState<TexturePlacement>({ ...PLACEMENT_FACTORY, ...initial });
  const [lightIndex, setLightIndex] = useState(lights.length ? 0 : -1);
  const set = sets.find((t) => t.id === placement.id);
  const light = lightIndex >= 0 ? lights[lightIndex] ?? null : null;
  const change = (key: keyof Omit<TexturePlacement, 'id'>, v: number) => setPlacement((p) => ({ ...p, [key]: v }));
  const nameOf = (file: string) => hdris.find((h) => h.file === file)?.name || file;

  return (
    <DesignerModal title={F.tuner.title(set?.name ?? placement.id, spaceLabel)} onClose={onClose} width={1020} icon={<SlidersHorizontal size={16} color="#FEB100" />}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 260px', maxWidth: 320, minWidth: 0 }}>
          <div className="rc-row">
            <div className="rc-row-head">
              <span className="rc-label">{T.set}</span>
              <select className="rc-select" aria-label={T.set} value={placement.id} onChange={(e) => setPlacement((p) => ({ ...p, id: e.target.value }))}>
                {sets.map((t) => <option key={t.id} value={t.id}>{t.name} · {P.textureSize(t.dimensions[0] / 304.8, t.dimensions[1] / 304.8)}</option>)}
                {!set && <option value={placement.id}>{placement.id}</option>}
              </select>
            </div>
          </div>
          {FIELDS.map(({ key, range, step, unit }) => (
            <MiniSlider
              key={key} label={(P.finishField as Record<string, string>)[key] ?? key} hint={(T.fieldHints as Record<string, string>)[key]} value={placement[key]} range={range} step={step} unit={unit}
              onChange={(v) => change(key, v)} onReset={placement[key] !== PLACEMENT_FACTORY[key] ? () => change(key, PLACEMENT_FACTORY[key]) : undefined}
            />
          ))}
          <div className="rc-row">
            <div className="rc-row-head">
              <span className="rc-label" title={F.tuner.lightHint}>{F.tuner.light}</span>
              <select className="rc-select" aria-label={F.tuner.light} value={lightIndex} onChange={(e) => setLightIndex(Number(e.target.value))}>
                {lights.map((l, i) => <option key={`${l.file}-${i}`} value={i}>{nameOf(l.file)}</option>)}
                <option value={-1}>{F.tuner.studio}</option>
              </select>
            </div>
          </div>
          <div className="rc-actions" style={{ marginTop: 12 }}>
            <button type="button" className="rc-action is-active" disabled={!set} onClick={() => { onSave(placement); onClose(); }}>{F.tuner.save}</button>
            <button type="button" className="rc-action" onClick={onClose}>{F.tuner.cancel}</button>
          </div>
          <p className="rc-note" style={{ marginTop: 10 }}>{F.tuner.note}</p>
        </div>
        <div style={{ flex: '3 1 420px', minWidth: 0 }}>
          {set
            ? <FloorPreview set={set} placement={placement} light={light} />
            : <p className="rc-note is-bad">{F.missing}</p>}
          <p className="rc-note" style={{ marginTop: 6 }}>{F.tuner.drag}</p>
        </div>
      </div>
    </DesignerModal>
  );
};

export default FloorTuner;
